/**
 * SetupTab — ERP Workspace "Setup & Stats" tab.
 * Shows catalog statistics, admin action buttons, and an unmapped-screens grid.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Boxes, RefreshCw, Database, CheckCircle2, AlertTriangle,
  Zap, Layers3, Monitor, Globe, Building2, Search,
  ChevronLeft, ChevronRight, Code2, X,
} from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';
import { StatCard } from './ERPShared';

const UM_PAGE_SIZE = 25;

export function SetupTab() {
  const [stats,      setStats]      = useState(null);
  const [statsLoad,  setStatsLoad]  = useState(true);
  const [initStatus, setInitStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  // Unmapped screens panel
  const [modules,        setModules]        = useState([]);
  const [umRows,         setUmRows]         = useState([]);
  const [umTotal,        setUmTotal]        = useState(0);
  const [umLoading,      setUmLoading]      = useState(false);
  const [umPage,         setUmPage]         = useState(1);
  const [umModule,       setUmModule]       = useState(null);
  const [umSearchInput,  setUmSearchInput]  = useState('');
  const [umSearchActive, setUmSearchActive] = useState('');

  const loadStats = useCallback(async () => {
    setStatsLoad(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/stats`);
      setStats(await r.json());
    } catch (e) { erpToast.error(e.message); }
    setStatsLoad(false);
  }, []);

  const loadModules = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/modules`);
      const d = await r.json();
      if (Array.isArray(d)) setModules(d);
    } catch (_) {}
  }, []);

  const loadUnmapped = useCallback(async (page, moduleId, searchQ) => {
    setUmLoading(true);
    try {
      const params = new URLSearchParams({
        notMapped: '1', industryCode: 'SHARED', page, pageSize: UM_PAGE_SIZE,
      });
      if (moduleId) params.set('moduleId', String(moduleId));
      if (searchQ && searchQ.length >= 2) params.set('search', searchQ);
      const r = await authFetch(`${API}/erp-workspace/admin/component-map?${params}`);
      const d = await r.json();
      if (d.rows !== undefined) { setUmRows(d.rows); setUmTotal(d.total); }
      else { const arr = Array.isArray(d) ? d : []; setUmRows(arr); setUmTotal(arr.length); }
    } catch (e) { erpToast.error(e.message); }
    setUmLoading(false);
  }, []);

  useEffect(() => { loadStats(); loadModules(); }, [loadStats, loadModules]);
  useEffect(() => { loadUnmapped(umPage, umModule, umSearchActive); }, [loadUnmapped, umPage, umModule, umSearchActive]);

  const initTables = async () => {
    setInitStatus('running');
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/init-tables`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) { setInitStatus({ ok: true, msg: d.message }); erpToast.success('Tables verified / created'); }
      else       { setInitStatus({ ok: false, msg: d.error }); }
    } catch (e) { setInitStatus({ ok: false, msg: e.message }); }
  };

  const syncUAMS = async () => {
    setSyncStatus('running');
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/sync`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) {
        setSyncStatus({ ok: true, merged: d.merged, at: d.at });
        await loadStats();
        loadUnmapped(1, umModule, umSearchActive);
        erpToast.success('UAMS seed complete');
      } else { setSyncStatus({ ok: false, msg: d.error }); }
    } catch (e) { setSyncStatus({ ok: false, msg: e.message }); }
  };

  const setModuleFilter = (mid) => { setUmModule(mid); setUmPage(1); };
  const doSearch   = () => { setUmSearchActive(umSearchInput); setUmPage(1); };
  const clearSearch = () => { setUmSearchInput(''); setUmSearchActive(''); setUmPage(1); };

  const totalPages = Math.max(1, Math.ceil(umTotal / UM_PAGE_SIZE));
  const mappedPct  = stats?.screens ? Math.round(((stats.mapped || 0) / stats.screens) * 100) : 0;

  // Module-wise unmapped counts from stats
  const unmappedByModule = useMemo(() => {
    const m = {};
    (stats?.moduleUnmappedCounts || []).forEach(item => { m[item.ModuleId] = item.unmapped; });
    return m;
  }, [stats]);

  const pillStyle = (active) => ({
    padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700, borderRadius: 10,
    cursor: 'pointer', border: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    background: active ? 'var(--erp-primary,#3b82f6)' : 'var(--surface,#fff)',
    color: active ? '#fff' : 'var(--text-secondary)',
    boxShadow: active ? 'none' : '0 0 0 1px var(--border)',
    transition: 'all .12s',
  });

  const th = {
    padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#64748b',
    fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '.04em',
    whiteSpace: 'nowrap', background: 'var(--surface-2,#f8fafc)',
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Stats banner ──────────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          Catalog Statistics
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatCard label="Modules"          value={statsLoad ? '…' : stats?.modules}          color="#3b82f6"  icon={Boxes} />
          <StatCard label="Sub-Modules"      value={statsLoad ? '…' : stats?.subModules}       color="#06b6d4"  icon={Layers3} />
          <StatCard label="Total Screens"    value={statsLoad ? '…' : stats?.screens}          color="#8b5cf6"  icon={Monitor} />
          <StatCard label="Mapped"           value={statsLoad ? '…' : stats?.mapped}           color="#22c55e"  icon={Code2} />
          <StatCard label="Implemented"      value={statsLoad ? '…' : stats?.implemented}      color="#10b981"  icon={CheckCircle2} />
          <StatCard label="Unmapped"         value={statsLoad ? '…' : stats?.unmapped}         color="#f59e0b"  icon={AlertTriangle} />
          <StatCard label="Country-Scoped"   value={statsLoad ? '…' : stats?.countryScoped}    color="#ec4899"  icon={Globe} />
          <StatCard label="Ind. Mappings"    value={statsLoad ? '…' : stats?.industryMappings} color="#ec4899"  icon={Building2} />
          <StatCard label="Country Mappings" value={statsLoad ? '…' : stats?.countryMappings}  color="#14b8a6"  icon={Globe} />
        </div>

        {/* Progress bar */}
        {!statsLoad && stats?.screens > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Component mapping progress</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: mappedPct >= 80 ? '#22c55e' : mappedPct >= 40 ? '#f59e0b' : '#ef4444' }}>
                {mappedPct}% &nbsp;({stats.mapped ?? 0} / {stats.screens} screens)
              </span>
            </div>
            <div style={{ height: 7, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${mappedPct}%`,
                background: mappedPct >= 80 ? 'linear-gradient(90deg,#22c55e,#10b981)' : mappedPct >= 40 ? 'linear-gradient(90deg,#f59e0b,#f97316)' : 'linear-gradient(90deg,#ef4444,#f97316)',
                borderRadius: 10, transition: 'width .4s',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Actions ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

        <div style={{ flex: '1 1 300px', padding: 16, background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Database size={14} color="#3b82f6" /> Initialize Tables
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Creates all 9 ERP Workspace tables in <code>RC26masterDB</code> (WebApp schema) if they don't exist. Safe to run multiple times.
          </div>
          <button className="erp-btn erp-btn-primary" onClick={initTables}
            disabled={initStatus === 'running'}
            data-testid="erpws-init-tables-btn"
            style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5 }}>
            {initStatus === 'running'
              ? <><RefreshCw size={12} className="spin" /> Running…</>
              : <><Database size={12} /> Init Tables</>}
          </button>
          {initStatus && initStatus !== 'running' && (
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: initStatus.ok ? '#22c55e' : '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
              {initStatus.ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              {initStatus.msg}
            </div>
          )}
        </div>

        <div style={{ flex: '1 1 300px', padding: 16, background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} color="#f59e0b" /> Seed from UAMS_Screens
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Ensures every screen in <code>UAMS_Screens</code> (SHARED) has a corresponding meta row in <code>ERPScreenSources</code>. Idempotent — safe to re-run.
          </div>
          <button className="erp-btn erp-btn-primary" onClick={syncUAMS}
            disabled={syncStatus === 'running'}
            data-testid="erpws-sync-btn"
            style={{ background: '#f59e0b', borderColor: '#f59e0b', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5 }}>
            {syncStatus === 'running'
              ? <><RefreshCw size={12} className="spin" /> Syncing…</>
              : <><Zap size={12} /> Sync Now</>}
          </button>
          {syncStatus && syncStatus !== 'running' && syncStatus.ok && (
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#22c55e' }}>
              <CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Seeded: {syncStatus.merged?.seedInserted || 0} new meta rows · {syncStatus.merged?.sourcesMerged || 0} sources merged
              {syncStatus.merged?.countryColAdded && <span style={{ marginLeft: 6, color: '#3b82f6' }}>+ CountryCode column added</span>}
            </div>
          )}
          {syncStatus && syncStatus !== 'running' && !syncStatus.ok && (
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#ef4444' }}>
              <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{syncStatus.msg}
            </div>
          )}
        </div>

      </div>

      <div style={{ textAlign: 'right' }}>
        <button className="erp-btn" onClick={loadStats} data-testid="erpws-refresh-stats"
          style={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={11} /> Refresh Stats
        </button>
      </div>

      {/* ── Unmapped Screens Grid ─────────────────────────────────── */}
      <div style={{ borderTop: '2px solid var(--border)', paddingTop: 18 }}>

        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 5 }}>
            <AlertTriangle size={13} color="#f59e0b" />
            Unmapped Screens
          </div>
          {!umLoading && (
            <span style={{
              fontSize: '0.7rem', fontWeight: 700, color: '#92400e',
              background: 'rgba(245,158,11,.1)', padding: '2px 8px', borderRadius: 10,
            }}>
              {umTotal} need component mapping
            </span>
          )}
          <div style={{ flex: 1 }} />
          {/* Search */}
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <input
              value={umSearchInput}
              onChange={e => setUmSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Search screens…"
              data-testid="um-search-input"
              style={{ fontSize: '0.72rem', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, width: 160 }}
            />
            {umSearchActive ? (
              <button className="erp-btn" onClick={clearSearch}
                style={{ fontSize: '0.72rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 3 }}>
                <X size={10} /> Clear
              </button>
            ) : (
              <button className="erp-btn" onClick={doSearch}
                style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Search size={10} /> Search
              </button>
            )}
          </div>
        </div>

        {/* Module filter pills */}
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 6, marginBottom: 10 }}>
          <button onClick={() => setModuleFilter(null)} style={pillStyle(!umModule)} data-testid="um-mod-all">
            All Modules
          </button>
          {modules.map(m => (
            <button key={m.ModuleId}
              onClick={() => setModuleFilter(m.ModuleId)}
              style={pillStyle(umModule === m.ModuleId)}
              data-testid={`um-mod-${m.ModuleId}`}>
              {m.ModuleName}
              {unmappedByModule[m.ModuleId] > 0 && (
                <span style={{
                  marginLeft: 5,
                  fontSize: '0.62rem', fontWeight: 800,
                  background: umModule === m.ModuleId ? 'rgba(255,255,255,.3)' : 'rgba(245,158,11,.18)',
                  color: umModule === m.ModuleId ? '#fff' : '#92400e',
                  padding: '0px 5px', borderRadius: 8, verticalAlign: 'middle',
                }}>
                  {unmappedByModule[m.ModuleId]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr>
                <th style={th}>Screen ID</th>
                <th style={th}>Screen Title</th>
                <th style={th}>Module</th>
                <th style={th}>Sub-Module</th>
                <th style={th}>Type</th>
              </tr>
            </thead>
            <tbody>
              {umLoading ? (
                <tr>
                  <td colSpan={5} style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <RefreshCw size={14} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Loading…
                  </td>
                </tr>
              ) : umRows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 28, textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>
                    <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {umSearchActive ? `No screens match "${umSearchActive}"` : 'All screens in this module are mapped!'}
                  </td>
                </tr>
              ) : umRows.map((row, idx) => (
                <tr key={row.ScreenId} style={{
                  borderBottom: '1px solid var(--border)',
                  background: idx % 2 === 0 ? 'transparent' : 'var(--surface-2,#f8fafc)',
                  transition: 'background .1s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'var(--surface-2,#f8fafc)'}
                >
                  <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>
                    {row.ScreenId}
                  </td>
                  <td style={{ padding: '5px 10px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.ScreenTitle || row.ScreenMenuName || '—'}
                  </td>
                  <td style={{ padding: '5px 10px', fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap' }}>
                    {row.ModuleName ? `${row.ModuleId} · ${row.ModuleName}` : `M${row.ModuleId}`}
                  </td>
                  <td style={{ padding: '5px 10px', fontSize: '0.72rem', color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.SubModuleName || row.SubModuleId || '—'}
                  </td>
                  <td style={{ padding: '5px 10px' }}>
                    {row.ScreenType
                      ? <span style={{ fontSize: '0.63rem', padding: '1px 5px', borderRadius: 3, background: 'var(--surface-3,#f1f5f9)', color: '#475569' }}>{row.ScreenType}</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              Page {umPage} of {totalPages} &nbsp;({umTotal} total)
            </span>
            <button className="erp-btn"
              onClick={() => setUmPage(p => Math.max(1, p - 1))}
              disabled={umPage <= 1 || umLoading}
              style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 2 }}>
              <ChevronLeft size={12} /> Prev
            </button>
            <button className="erp-btn"
              onClick={() => setUmPage(p => Math.min(totalPages, p + 1))}
              disabled={umPage >= totalPages || umLoading}
              style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 2 }}>
              Next <ChevronRight size={12} />
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
